<template>
	<div class="main">
		<header class="page-title">
			<h2>{{ provider.name }} &ndash; Properties</h2>
			<div class="ml-auto">
				<a @click.prevent="saveProperties" class="btn btn-md btn-success" href="#"><i class="fas fa-save"></i> Save Changes</a>
			</div>
		</header>
		<div v-if="!loaded" class="flex center text-center mb-1">
			<i class="fas fa-cog fa-spin fa-2x"></i>
			Loading, please do not press anything...
		</div>
		<div v-for="group in availableProperties" v-if="loaded">
			<h3 class="h4">{{ group.name }}</h3>
			<div class="table-responsive">
				<table class="table-properties">
					<thead>
						<tr>
							<th>Property Name</th>
							<th>Value</th>
							<th>Unlimited</th>
							<th v-if="debugMode">Min/Med/Max</th>
							<th v-if="debugMode">Weight (%)</th>
							<th>Calc. Score: {{calculatedScore}} <small title="formula here"></small></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="property in group.properties">
							<td>
								{{ property.name }} (#{{property.id}})
								<span class="badge-warning badge" title="This property is visible on Provider Portal"
									  v-if="property.visible_on_provider_portal">PP</span>
							</td>
							<td>
								<input class="input-small" step=".01" type="number" v-if="property.type === 'number'" v-model="providerProperties[property.id].value" @change="updateNormalizedValue(property.id)" :disabled="userRoles.includes('users.translator')">
								<div class="switches" v-if="property.type === 'checkbox'">
									<input :id="property.id + '-1'" :name="property.id" class="switch-na" type="radio" v-model="providerProperties[property.id].value" value="null" @change="updateNormalizedValue(property.id)" :disabled="userRoles.includes('users.translator')">
									<label :for="property.id + '-1'" class="label-checkbox">N/A</label>
									<input :id="property.id + '-2'" :name="property.id" class="switch-true" type="radio" v-model="providerProperties[property.id].value" value="1" @change="updateNormalizedValue(property.id)" :disabled="userRoles.includes('users.translator')">
									<label :for="property.id + '-2'" class="label-checkbox">True</label>
									<input :id="property.id + '-3'" :name="property.id" class="switch-false" type="radio" v-model="providerProperties[property.id].value" value="0" @change="updateNormalizedValue(property.id)" :disabled="userRoles.includes('users.translator')">
									<label :for="property.id + '-3'" class="label-checkbox">False</label>
								</div>
								<div class="input-group" v-if="property.type === 'text'">
									<input @keypress="canEdit" class="input-small" type="text" v-model="providerProperties[property.id].value['en'].value" :disabled="userRoles.includes('users.translator')">
									<a @click.prevent="showModal(providerProperties[property.id])" class="btn btn-md btn-primary" href="#"><i class="fas fa-globe-americas"></i></a>
								</div>
								<div class="input-group" v-if="property.type === 'combobox'">
									<select id="property-params" multiple v-model="providerProperties[property.id].combobox_values">
										<option v-for="param in property.params" :value="param.id">{{ param.key }}</option>
									</select>
								</div>
							</td>
							<td>
								<input type="checkbox" v-if="property.type === 'number'" v-model="providerProperties[property.id].unlimited" :disabled="userRoles.includes('users.translator')">
							</td>
							<td v-if="debugMode">{{providerProperties[property.id].min}}/{{providerProperties[property.id].median}}/{{providerProperties[property.id].max}}</td>
							<td v-if="debugMode">{{providerProperties[property.id].defaultWeight}}</td>
							<td>{{providerProperties[property.id].score}}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
		<div class="modal-bg" v-show="translationModal">
			<div class="modal">
				<header class="modal-header">
					<h4 class="modal-title">Edit Translations</h4>
					<a @click.prevent="hideModal" class="modal-close" title="Close modal"><i class="fas fa-times"></i></a>
				</header>
				<section class="modal-body">
					<div class="card mb-0">
						<header class="card-header language-list">
							<lang-selector></lang-selector>
						</header>
						<div class="card-body">
							<div class="form-group">
								<label for="trans">Translation</label>
								<input @keypress="canEdit" id="trans" type="text" v-model="selectedProperty.value[clickedLang].value" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
							</div>
						</div>
					</div>
				</section>
				<footer class="modal-footer">
					<a @click.prevent="hideModal" class="btn btn-md btn-success" href="#"><i class="fas fa-check"></i> Confirm</a>
				</footer>
			</div>
		</div>
	</div>
</template>

<script>
	import {eventBus} from '../../app';
	import Errors from '../../Utils/Errors.js';

	export default {
		props: {
			providerid: {
				type: Number
			}
		},
		data() {
			return {
				debugMode: true,
				tabName: 'providerProperties',
				providerId: this.providerid,
				loaded: false,
				provider: {},
				availableProperties: {},
				providerProperties: {},
				originalProperties: {},
				calculatedScore: 0,

				defaultLocale: this.$defaultLanguage.locale,
				userRoles: this.$currentUserRoles,
				translationModal: false,
				selectedProperty: {
					value: []
				},

				clickedLang: this.$defaultLanguage.locale,
				errors: new Errors(),
			}
		},
		created() {
			this.fetchDataFromApi();
			let vm = this;

			for (let i = 0; i < vm.$availableLanguages.length; i++) {
				vm.selectedProperty.value[vm.$availableLanguages[i].locale] = {
					value: null
				}
			}

			// When setActiveStatus method ran, change selected data in form fields
			/* From LangSelector */
			eventBus.$on('setActiveStatus', function (clickedLang) {
				vm.clickedLang = clickedLang;
			});
		},
		methods: {
			fetchDataFromApi: function () {
				let vm = this;
				axios.post('/api/' + vm.$selectedSite.slug + '/providers/fetchtabdata/' + vm.providerId, {
					tab: vm.tabName
				})
					.then(response => {
						if (response.data.hasOwnProperty('provider')) {
							vm.provider = response.data.provider;
						}
						if (response.data.hasOwnProperty('availableProperties')) {
							vm.availableProperties = response.data.availableProperties;
						}
						if (response.data.hasOwnProperty('providerProperties')) {
							vm.changeCounter = 0;
							vm.providerProperties = response.data.providerProperties;
							vm.originalProperties = JSON.parse(JSON.stringify(response.data.providerProperties));
							vm.calculateScore();
						}
						vm.loaded = true;
					});
				/*.catch(e => {
					vm.$toastr.error('Api ' + vm.tabName + ' tab load failed', 'Error');
				});*/
			},
			calculateScore: function () {
				this.calculatedScore = 0;
				for (let property in this.providerProperties) {
					if (typeof this.providerProperties[property].score === 'number') {
						this.calculatedScore += this.providerProperties[property].score;
					}
					if (Array.isArray(this.providerProperties[property].combobox_values) && this.providerProperties[property].combobox_values.length > 0) {
						this.calculatedScore += this.providerProperties[property].combobox_values.reduce(function (total, current) { return parseInt(total) + parseInt(current) });
					}
				}
				this.calculatedScore = Math.round(this.calculatedScore * 100) / 100;
			},
			updateNormalizedValue: function (id) {
				this.providerProperties[id].normalizedValue = this.providerProperties[id].value;
			},
			showModal: function (property) {
				let vm = this;
				vm.translationModal = true;
				vm.selectedProperty = property;
			},
			hideModal: function () {
				let vm = this;
				vm.translationModal = false;
			},
			saveProperties: function () {
				let vm = this;

				let currentDateWithFormat = new Date().toJSON().slice(0, 10).split('-').reverse().join('/')
				this.$dialog.confirm('Do you want to update the displayed date of the article to ' + currentDateWithFormat + '?', {
					okText: 'Yes',
					cancelText: 'No',
				}).then(function () {
					vm.changeDisplayDate(true);
				}).catch(function () {
					vm.changeDisplayDate(false);
				});

			},
			changeDisplayDate: function (data) {
				let vm = this;
				this.loaded = false;
				axios.post('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/properties', {
					new: vm.providerProperties,
					original: vm.originalProperties,
					display_date: data
				}).then(response => {
					vm.$toastr.success('Properties saved successfully. Score recalculation still in progress, result will be available shortly.');
					vm.loaded = true;
					vm.fetchDataFromApi();
				}).catch(e => {
					vm.errors.record(e.response.data);
					vm.$toastr.error('Save properties failed.', 'Error');
					vm.loaded = true;
				});
			},
			canEdit: function (event) {
				let vm = this;
				if(vm.userRoles.includes('users.translator') && vm.clickedLang === vm.defaultLocale) {
					event.preventDefault();
					event.stopPropagation();
				}
			}
		}
	}
</script>

<style scoped>

</style>
